import axios from 'axios';
import { BaseUrl } from '../Constants/Constants';

// Feedback Forms API Service - Updated for real backend integration
export const FeedbackFormsAPI = {
  // Get all feedback forms
  getAllForms: async () => {
    try {
      const response = await axios.get(`${BaseUrl}feedback-form`, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch feedback forms: ${error.message}`);
    }
  },

  // Get feedback form by ID
  getFormById: async (formId) => {
    try {
      const response = await axios.post(`${BaseUrl}feedback-form/feedback_form_details`, 
        { feedback_form_id: formId }, 
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch feedback form: ${error.message}`);
    }
  },

  // Create new feedback form
  createForm: async (formData) => {
    try {
      const response = await axios.post(`${BaseUrl}feedback-form/add`, formData, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to create feedback form: ${error.message}`);
    }
  },

  // Update feedback form
  updateForm: async (formId, formData) => {
    try {
      const response = await axios.post(`${BaseUrl}feedback-form/feedback_form_update`, 
        { feedback_form_id: formId, ...formData }, 
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to update feedback form: ${error.message}`);
    }
  },

  // Delete feedback form
  deleteForm: async (formId, modifiedBy) => {
    try {
      const params = modifiedBy ? { modified_by: modifiedBy } : {};
      const response = await axios.delete(`${BaseUrl}feedback-form/${formId}`, {
        params,
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      throw new Error(`Failed to delete feedback form: ${error.message}`);
    }
  },

  // Get forms by course
  getFormsByCourse: async (courseId) => {
    try {
      const response = await axios.post(`${BaseUrl}feedback-form/list_of_feedback_forms`, 
        { course_id: courseId }, 
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch forms by course: ${error.message}`);
    }
  },

  // Get course reports
  getCourseReports: async (courseId) => {
    try {
      const response = await axios.post(`${BaseUrl}feedback-form/feedback_reports`, 
        { course_id: courseId }, 
        { withCredentials: true }
      );
      return response.data;
    } catch (error) {
      throw new Error(`Failed to fetch course reports: ${error.message}`);
    }
  }
};

// Form validation utilities
export const FormValidation = {
  validateFormName: (name) => {
    if (!name || !name.trim()) {
      return 'Form name is required';
    }
    if (name.length < 3) {
      return 'Form name must be at least 3 characters long';
    }
    if (name.length > 100) {
      return 'Form name must be less than 100 characters';
    }
    return null;
  },

  validateQuestion: (question) => {
    if (!question.question || !question.question.trim()) {
      return 'Question text is required';
    }
    if (question.question.length > 500) {
      return 'Question text must be less than 500 characters';
    }
    if (question.type !== 'text' && (!question.options || question.options.length < 2)) {
      return 'At least 2 options are required for this question type';
    }
    if (question.options) {
      const emptyOptions = question.options.filter(opt => !opt || !opt.trim());
      if (emptyOptions.length > 0) {
        return 'All options must have text';
      }
    }
    return null;
  },

  validateForm: (formData) => {
    const errors = [];
    
    // Validate form name
    const nameError = FormValidation.validateFormName(formData.name);
    if (nameError) errors.push(nameError);

    // Validate questions
    if (!formData.questions || formData.questions.length === 0) {
      errors.push('At least one question is required');
    } else {
      formData.questions.forEach((question, index) => {
        const questionError = FormValidation.validateQuestion(question);
        if (questionError) {
          errors.push(`Question ${index + 1}: ${questionError}`);
        }
      });
    }

    return errors;
  }
};

// Form type configurations - Updated to match API
export const FormTypes = {
  DEFAULT: {
    value: 'default',
    label: 'Default Form',
    description: 'Predefined set of standard feedback questions',
    maxQuestions: 20,
    maxOptions: 10
  },
  MULTIPLE_CHOICE: {
    value: 'multiple_choice',
    label: 'Multiple-Choice Custom Form',
    description: 'Create custom questions with multiple-choice options',
    maxQuestions: 20,
    maxOptions: 10
  },
  YES_NO: {
    value: 'yes_no',
    label: 'Yes/No Form',
    description: 'Create feedback forms containing only Yes/No type questions',
    maxQuestions: 20,
    maxOptions: 2
  }
};

// Default questions for default form type - Updated to match API structure
export const DefaultQuestions = [
  {
    id: 1,
    text: 'How would you rate the overall quality of this course?',
    type: 'rating',
    required: true,
    order: 1,
    options: ['1 - Poor', '2 - Fair', '3 - Good', '4 - Very Good', '5 - Excellent']
  },
  {
    id: 2,
    text: 'How satisfied are you with the course content?',
    type: 'rating',
    required: true,
    order: 2,
    options: ['1 - Very Dissatisfied', '2 - Dissatisfied', '3 - Neutral', '4 - Satisfied', '5 - Very Satisfied']
  },
  {
    id: 3,
    text: 'What did you like most about this course?',
    type: 'text',
    required: false,
    order: 3,
    options: []
  },
  {
    id: 4,
    text: 'What improvements would you suggest for this course?',
    type: 'text',
    required: false,
    order: 4,
    options: []
  },
  {
    id: 5,
    text: 'How likely are you to recommend this course to others?',
    type: 'rating',
    required: true,
    order: 5,
    options: ['1 - Very Unlikely', '2 - Unlikely', '3 - Neutral', '4 - Likely', '5 - Very Likely']
  }
];

export default FeedbackFormsAPI;
